import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  CameraAngle,
  Game,
  GameVideoSource,
  Video,
  VideoVariantEnum
} from '../../domain/game';

@Component({
  selector: 'app-game-details-videos-card',
  templateUrl: './game-details-videos-card.component.html',
  styleUrls: ['./game-details-videos-card.component.css']
})
export class GameDetailsVideosCardComponent implements OnChanges {
  @Input() game: Game;
  @Input() saving = false;
  @Input() isSyncingLiveStreams = false;
  readonly cameras = Object.entries(CameraAngle);
  readonly variants = Object.values(VideoVariantEnum);
  private expandedSources = new Set<string>();

  @Output() detailsChange = new EventEmitter<void>();
  @Output() addVideo = new EventEmitter<number>();
  @Output() removeVideo = new EventEmitter<Video>();
  @Output() downloadVideo = new EventEmitter<Video>();
  @Output() videoUrlChange = new EventEmitter<Video>();
  @Output() uploadVideo = new EventEmitter<Video | undefined>();
  @Output() liveStreamAdded = new EventEmitter<Video>();
  @Output() syncLiveStreams = new EventEmitter<void>();

  ngOnChanges(changes: SimpleChanges) {
    if (changes['game']) {
      this.resetVideoSourceExpansion();
    }
  }

  /**
   * Whether a camera sync can run: a live 'main' camera plus at least one more,
   * as the server requires (camera-auto-sync.service.ts)
   */
  get canSyncLiveStreams(): boolean {
    const cameras = (this.game?.videos ?? []).filter(
      (v) =>
        v.format === 'hls' && v.variant === 'liverecording' && v.cameraAngle
    );
    return cameras.length >= 2 && cameras.some((v) => v.cameraAngle === 'main');
  }

  videoDownloadAvailable(video: Video) {
    return this.game?._id && video?.url;
  }

  /**
   * Whether the source has anything to show beyond its summary line. A direct
   * upload with no notes has nothing to open (nothing is signed in to, so there
   * is no credential line either), so it stays a plain line rather than a
   * control that does nothing.
   */
  hasDetails(source: GameVideoSource): boolean {
    return !!(source.loginUrl || source.notes || this.showsCredentials(source));
  }

  /**
   * Whether there is anything to say about a login: one is needed, or one is on
   * file. A stored login still gets a line after the customer stops requiring
   * one, so it doesn't disappear from the card while it is still there.
   */
  showsCredentials(source: GameVideoSource): boolean {
    return source.needsCredentials || source.hasCredentials;
  }

  /**
   * What to say about the login when there is no link to point at: either it is
   * on file and this user may not see where, or the customer still owes it to
   * us — which is the operator's cue to go and ask rather than keep looking.
   */
  credentialsLabel(source: GameVideoSource): string {
    return source.hasCredentials
      ? 'stored - only game operators have access to it'
      : 'not yet provided by the customer';
  }

  isExpanded(source: GameVideoSource): boolean {
    return this.expandedSources.has(source.publicId);
  }

  /**
   * Ties the expand button to the panel it opens for screen readers. public_id
   * is a uuid, so it is unique across the sources on the page.
   */
  detailsId(source: GameVideoSource): string {
    return `video-source-details-${source.publicId}`;
  }

  toggleSource(source: GameVideoSource) {
    if (this.expandedSources.has(source.publicId)) {
      this.expandedSources.delete(source.publicId);
    } else {
      this.expandedSources.add(source.publicId);
    }
  }

  /**
   * A picked source starts open only while the game has no video yet — that is
   * when the operator still has to go and fetch it.
   *
   * Season sources stay closed: there can be any number of them, and opening
   * them all would bury the video table.
   *
   * Keyed off the game input rather than the video list, so adding the first
   * video doesn't fold away a panel the operator is reading from.
   *
   * "Has a video" means one with a url: an empty row is the placeholder
   * addVideo() inserts, which is the operator still looking for the video.
   */
  private resetVideoSourceExpansion() {
    this.expandedSources.clear();
    if (this.game?.videos?.some((video) => video.url)) {
      return;
    }
    for (const source of this.game?.videoSources ?? []) {
      if (source.selected) {
        this.expandedSources.add(source.publicId);
      }
    }
  }

  /**
   * The side the customer's video source belongs to, named with the team so the
   * operator doesn't have to map "home" back onto the game themselves. Empty
   * when the source's team is no longer one of the game's teams.
   */
  teamLabel(source: GameVideoSource): string {
    if (source.team === 'home') {
      return this.game?.homeTeam ? `Home - ${this.game.homeTeam}` : 'Home';
    }
    if (source.team === 'away') {
      return this.game?.awayTeam ? `Away - ${this.game.awayTeam}` : 'Away';
    }
    return '';
  }

  /**
   * The platform the customer named, falling back to how the video reaches us:
   * a direct upload has no name of its own to show.
   */
  sourceName(source: GameVideoSource): string {
    return source.label
      ? `${source.label} (${source.platformLabel})`
      : source.platformLabel;
  }

  onDetailsChange() {
    this.detailsChange.emit();
  }

  onVideoUrlChange(video: Video) {
    this.videoUrlChange.emit(video);
    this.detailsChange.emit();
  }

  onAddVideo(index: number) {
    this.addVideo.emit(index);
    this.detailsChange.emit();
  }

  onRemoveVideo(video: Video) {
    this.removeVideo.emit(video);
    this.detailsChange.emit();
  }

  onLiveStreamAdded(video: Video) {
    this.liveStreamAdded.emit(video);
    this.detailsChange.emit();
  }

  displayName(camera: string) {
    return camera.replace(/_/g, ' ');
  }
}
